"use client";

import React, { useEffect, useState } from "react";

// @mui/material components
import Card from "@mui/material/Card";
import {
  CircularProgress,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Box,
  Chip,
} from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";

// Data
import OrdersApi from "api/orders";
import ActionCell from "./components/ActionCell";
import PaymentCell from "./components/PaymentCell";
import FactureCell from "./components/FactureCell";
import { useTranslation } from "react-i18next";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import DateRangeFilter from "components/Date/DateRange Filter";

function DeliveredOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [anchor, setAnchor] = useState(null); // Anchor for menu
  const [nameSortOrder, setNameSortOrder] = useState("asc");
  const [dateSortOrder, setDateSortOrder] = useState("asc");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("");
  const [filtersApplied, setFiltersApplied] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { t } = useTranslation();

  const handleGetOrders = (page) => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found in localStorage.");
      setError("No token found in localStorage.");
      setLoading(false);
      return;
    }

    setLoading(true);
    OrdersApi.GetDeliveredOrders(token, page)
      .then((response) => {
        if (response.data) {
          setOrders(response.data.results);
          setTotalPages(Math.ceil(response.data.count / 10));
        } else {
          console.warn("No data found:", response.data.msg);
        }
      })
      .catch((error) => {
        if (error.response) {
          console.error("Error response data:", error.response.data.msg);
        } else {
          console.error("There has been an error.");
        }
        setError(error.response?.data?.msg || error.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    handleGetOrders(page);
  }, [page]);

  useEffect(() => {
    // Count active filters
    let count = 0;
    if (startDate && endDate) count++;
    if (filterPaymentStatus) count++;
    if (filterPaymentMethod) count++;
    setFiltersApplied(count);
  }, [startDate, endDate, filterPaymentStatus, filterPaymentMethod]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleClearDateRange = () => {
    setStartDate("");
    setEndDate("");
  };

  const handleSortChange = (field) => {
    let newSortOrder;

    if (field === "username") {
      newSortOrder = nameSortOrder === "asc" ? "desc" : "asc";
      setNameSortOrder(newSortOrder);
      sortOrders("customer", newSortOrder);
    } else if (field === "created_at") {
      newSortOrder = dateSortOrder === "asc" ? "desc" : "asc";
      setDateSortOrder(newSortOrder);
      sortOrders("created_at", newSortOrder);
    }

    setAnchor(null); // Close the menu after sorting
  };

  const sortOrders = (field, order) => {
    const sortedOrders = [...orders].sort((a, b) => {
      const valueA =
        field === "created_at" ? new Date(a[field]) : a[field].toLowerCase();
      const valueB =
        field === "created_at" ? new Date(b[field]) : b[field].toLowerCase();

      if (order === "asc") {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });

    setOrders(sortedOrders); // Update the orders with the sorted array
  };

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setFilterPaymentStatus("");
    setFilterPaymentMethod("");
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return (
          <SoftBox
            display="flex"
            alignItems="center"
            px={1}
            py={0.5}
            borderRadius="lg"
            bgcolor="rgba(76, 175, 80, 0.1)"
          >
            <CheckCircleIcon
              sx={{ color: "success.main", fontSize: 16, mr: 1 }}
            />
            <SoftTypography
              variant="caption"
              color="success"
              fontWeight="medium"
            >
              Payé
            </SoftTypography>
          </SoftBox>
        );
      case "partially_paid":
        return (
          <SoftBox
            display="flex"
            alignItems="center"
            px={1}
            py={0.5}
            borderRadius="lg"
            bgcolor="rgba(255, 152, 0, 0.1)"
          >
            <AccessTimeIcon
              sx={{ color: "warning.main", fontSize: 16, mr: 1 }}
            />
            <SoftTypography
              variant="caption"
              color="warning"
              fontWeight="medium"
            >
              Partiellement payé
            </SoftTypography>
          </SoftBox>
        );
      default:
        return (
          <SoftBox
            display="flex"
            alignItems="center"
            px={1}
            py={0.5}
            borderRadius="lg"
            bgcolor="rgba(0, 0, 0, 0.05)"
          >
            <HelpOutlineIcon
              sx={{ color: "text.secondary", fontSize: 16, mr: 1 }}
            />
            <SoftTypography variant="caption" color="text" fontWeight="medium">
              -
            </SoftTypography>
          </SoftBox>
        );
    }
  };

  const columns = React.useMemo(
    () => [
      { Header: `${t("Client")}`, accessor: "customer" },
      { Header: `${t("Méthode de paiement")}`, accessor: "payment_methode" },
      { Header: `${t("Prix total")}`, accessor: "total_price" },
      { Header: `${t("Date de livraison")}`, accessor: "delvered_at" },
      { Header: `${t("Statut de paiement")}`, accessor: "payment_status" },
      { Header: `${t("Paiement")}`, accessor: "payment" },
      { Header: `${t("Bon de livraison")}`, accessor: "facture" },
      { Header: `${t("action")}`, accessor: "action" },
    ],
    [t]
  );

  const rows = React.useMemo(() => {
    return orders
      .filter((order) => {
        const dateMatches =
          startDate && endDate
            ? new Date(order.delvered_at) >= new Date(startDate) &&
              new Date(order.delvered_at) <= new Date(endDate)
            : true;

        const statusMatches = filterPaymentStatus
          ? order.payment_status === filterPaymentStatus
          : true;

        const methodMatches = filterPaymentMethod
          ? order.payment_methode?.toLowerCase() ===
            filterPaymentMethod.toLowerCase()
          : true;

        return dateMatches && statusMatches && methodMatches;
      })
      .map((order) => ({
        customer: order.customer,
        payment_methode: order.payment_methode,
        total_price: `${order.total_price} TND`,
        delvered_at: new Date(order.delvered_at).toLocaleString(),
        payment_status: getPaymentStatusLabel(order.payment_status),
        payment:
          Number.parseFloat(order.rest_to_pay) > 0 ||
          order.number_of_payments <= 1 ? (
            <PaymentCell orderId={order.id} disabled={false} />
          ) : (
            <PaymentCell orderId={order.id} disabled={true} />
          ),
        facture: <FactureCell delivery_note_id={order.delivery_note_id} />,
        action: <ActionCell orderId={order.id} />,
      }));
  }, [orders, startDate, endDate, filterPaymentStatus, filterPaymentMethod]);

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox my={3}>
          <SoftTypography color="error">{error}</SoftTypography>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        minHeight="85vh"
      >
        <SoftBox my={3}>
          {/* Ultra Premium Filter Section */}
          <Card
            sx={{
              mb: 3,
              borderRadius: "20px",
              boxShadow:
                "0 12px 24px -6px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 1px 0 rgba(0, 0, 0, 0.1)",
              overflow: "visible",
              background:
                "linear-gradient(to right, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 1))",
              backdropFilter: "blur(20px)",
              position: "relative",
            }}
          >
            <Box
              sx={{
                height: "auto",
                overflow: "hidden",
                transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  {/* Date Filter */}
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleDateRangeChange}
                    onClear={handleClearDateRange}
                  />
                  {/* Payment Status Filter */}
                  <Box sx={{ minWidth: 240 }}>
                    <SoftSelect
                      place
                      placeholder={t("Statut de paiement")}
                      holder={t("Statut de paiement")}
                      value={
                        filterPaymentStatus
                          ? {
                              value: filterPaymentStatus,
                              label:
                                filterPaymentStatus === "paid"
                                  ? t("Payé")
                                  : t("Partiellement payé"),
                            }
                          : null
                      }
                      onChange={(option) =>
                        setFilterPaymentStatus(option ? option.value : "")
                      }
                      options={[
                        { value: "paid", label: t("Payé") },
                        {
                          value: "partially_paid",
                          label: t("Partiellement payé"),
                        },
                      ]}
                      isClearable
                      menuPortalTarget={document.body}
                    />
                  </Box>
                  {/* Payment Method Filter */}
                  <Box>
                    <SoftSelect
                      placeholder={t("Méthode de paiement")}
                      value={
                        filterPaymentMethod
                          ? {
                              value: filterPaymentMethod,
                              label: filterPaymentMethod,
                            }
                          : null
                      }
                      onChange={(option) =>
                        setFilterPaymentMethod(option ? option.value : "")
                      }
                      options={[
                        { value: "cash", label: "Espèce" },
                        { value: "virement", label: "Virement" },
                        { value: "cheque", label: "Chèque" },
                      ]}
                      isClearable
                      menuPortalTarget={document.body}
                    />
                  </Box>
                </Box>
                {/* Sort Menu */}
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <SoftButton
                    variant="outlined"
                    color="dark"
                    disabled={orders.length === 0}
                    onClick={(e) => setAnchor(e.currentTarget)}
                  >
                    {t("Trier par nom ou date")} (⬆⬇)
                  </SoftButton>
                  <Menu
                    anchorEl={anchor}
                    open={Boolean(anchor)}
                    onClose={() => setAnchor(null)}
                  >
                    <MenuItem onClick={() => handleSortChange("username")}>
                      {t("Trier par Nom")} (
                      {nameSortOrder === "asc" ? "⬆" : "⬇"})
                    </MenuItem>
                    <MenuItem onClick={() => handleSortChange("created_at")}>
                      {t("Trier par Date")} (
                      {dateSortOrder === "asc" ? "⬆" : "⬇"})
                    </MenuItem>
                  </Menu>
                </SoftBox>
              </Box>

              {filtersApplied > 1 && (
                <Box
                  sx={{
                    px: 2.5,
                    pb: 2.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    label={t("Effacer tous les filtres")}
                    onDelete={resetFilters}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: "rgba(31, 81, 255, 0.08)",
                      color: "#1F51FF",
                      border: "1px solid rgba(31, 81, 255, 0.2)",
                      "& .MuiChip-deleteIcon": {
                        color: "#1F51FF",
                      },
                      "& .MuiChip-label": {
                        fontWeight: 500,
                      },
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(31, 81, 255, 0.12)",
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Card>

          <Card
            sx={{
              borderRadius: "20px",
              boxShadow:
                "0 12px 24px -6px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 1px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            {loading ? (
              <SoftBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={35}
              >
                <CircularProgress sx={{ color: "#1F51FF" }} />
              </SoftBox>
            ) : (
              <>
                <DataTable
                  table={{ columns, rows }}
                  entriesPerPage={false}
                  canSearch={orders.length > 0}
                  columnMinWidth={70}
                />
                {totalPages > 1 && (
                  <Stack
                    spacing={2}
                    sx={{
                      mt: 2,
                      position: "absolute",
                      bottom: 7,
                      left: 100,
                      transform: "translateX(-50%)",
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="secondary"
                    />
                  </Stack>
                )}
              </>
            )}
          </Card>
        </SoftBox>
        <Footer />
      </SoftBox>
    </DashboardLayout>
  );
}

export default DeliveredOrders;
